// NCP-5516 / Edit Identity: the walk this tab is part-way through. Every walk
// write belongs to an open walk — a new application, an edit from the quote
// page's Edit button, or the survey of a LOB added on the quote page — and
// carries its id; the server refuses a write from any other walk as out of
// date.
//
// The server owns the walk: createApplication, openApplicationEdit and the
// quote page's add-LOB open it, and nothing else does — a page landing only
// reads it (reconcileTabWalk). This tab holds its id, to send, and its kind,
// so the resubmit banner shows for an edit only.
//
// The edit spans several routes (Phase 2, then one Phase 3 survey per LOB) and
// the customer may refresh on any of them, so this cannot live in router state
// or component state. sessionStorage is the narrowest store that survives both:
// scoped to the tab, gone when it closes.
const APPLICATION_EDIT_ID_KEY = 'applicationEditId';
const APPLICATION_EDIT_KIND_KEY = 'applicationEditKind';

/**
 * Shown for the whole edit, on Phase 2 and on every Phase 3 LOB survey. There
 * is no dismiss control: the statement stays true until the walk is finished,
 * and a customer who closed it would be the one who most needs it.
 */
export const RESUBMIT_TO_SAVE_MESSAGE =
  'Please note: The application must be completed and resubmitted for any and all changes to be saved.';

/**
 * Shown when the walk finished but the quote changed on the quote page while it
 * was open, so the edit was not submitted.
 */
export const EDIT_CONFLICT_MESSAGE =
  'Your quote was changed while you were editing. Review it, then edit again if you still need to.';

/** Called with the walk the server returned: 'initial', 'edit' or 'add'. */
export const setApplicationEdit = (editId: string, kind: string): void => {
  sessionStorage.setItem(APPLICATION_EDIT_ID_KEY, editId);
  sessionStorage.setItem(APPLICATION_EDIT_KIND_KEY, kind);
};

/** The open walk's id, to send with every walk mutation; undefined outside one. */
export const getApplicationEditId = (): string | undefined =>
  sessionStorage.getItem(APPLICATION_EDIT_ID_KEY) ?? undefined;

/** Whether this tab is editing a submitted application: the banner's cue. */
export const isApplicationEditInProgress = (): boolean =>
  !!getApplicationEditId() &&
  sessionStorage.getItem(APPLICATION_EDIT_KIND_KEY) === 'edit';

/**
 * Called when the walk ends — completed or declined — when a brand new
 * application starts in the same tab, and when a landing finds no walk open, so
 * a stale id can never put the banner in front of someone applying for the
 * first time.
 */
export const clearApplicationEdit = (): void => {
  sessionStorage.removeItem(APPLICATION_EDIT_ID_KEY);
  sessionStorage.removeItem(APPLICATION_EDIT_KIND_KEY);
};

/** This tab's walk measured against the walk the server has open. */
export type TabWalk =
  /** The open walk is this tab's — joined, when the tab held none yet. */
  | { status: 'current' }
  /**
   * The tab cannot write any more: another walk replaced its walk (Edit in
   * another tab), or none is open (submitted, or an application started
   * before Edit Identity). `exitPath` is where the customer goes instead.
   */
  | { status: 'replaced' | 'closed'; exitPath: string };

export interface ServerWalk {
  applicationGroupId: string;
  hasSubmission: boolean;
  openEdit?: { editId: string; kind: string } | null;
}

/**
 * Compare this tab with the server's walk. A tab with no id joins the open walk
 * — a new tab on the same application, or the survey of a LOB the quote page
 * just added — and has lost nothing. A tab holding a different id is left
 * behind: joining would show it answers it never typed, and send them as its
 * own, so the customer is told instead.
 */
export const reconcileTabWalk = ({
  applicationGroupId,
  hasSubmission,
  openEdit
}: ServerWalk): TabWalk => {
  // With a quote, the quote page shows what was submitted; without one, the
  // application can only be started again.
  const exitPath = hasSubmission ? `/quote/${applicationGroupId}` : '/';
  if (!openEdit) {
    clearApplicationEdit();
    return { status: 'closed', exitPath };
  }
  const tabEditId = getApplicationEditId();
  if (tabEditId && tabEditId !== String(openEdit.editId)) {
    return { status: 'replaced', exitPath };
  }
  setApplicationEdit(String(openEdit.editId), openEdit.kind);
  return { status: 'current' };
};

/** What the customer reads when the tab cannot write any more. */
export const walkExitMessage = (
  status: 'replaced' | 'closed',
  exitPath: string
): string => {
  if (status === 'replaced') {
    return 'This application was opened in another tab or window, so changes here can’t be saved.';
  }
  return exitPath === '/'
    ? 'This application can no longer be continued. Please start a new application.'
    : 'This application has already been submitted.';
};
